/**
 * Keybinds, made simple.
 *
 * Every action is listed, grouped, with what it does. Click the key box to
 * record - press the combination, Esc to cancel, Backspace to clear. "Use
 * recommended" fills in sensible keys for everything still empty in one go.
 *
 * Nothing listens until it has a key, and a combination can only do one
 * thing: a clash is explained on the row instead of silently stealing it.
 * The call controls also work while Pugo is behind a game.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  ACTIONS, BUILTIN, GROUPS, actionLabel, comboOf, comboOwner, comboParts, isGlobalAction, type ActionDef
} from '@/lib/keybinds';
import { cn } from '@/lib/cn';
import { isDesktop } from '@/lib/bridge';
import { useHotkeyFailures } from '@/lib/globalKeys';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { useConfirm, useToast } from './ui';

const CARD = 'pugo-panel rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-4 shadow-[inset_0_0_0_1px_var(--line-soft)]';

export function Keybinds() {
  const settings = useSession((s) => s.settings);
  const saveSettings = useSession((s) => s.saveSettings);
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [recording, setRecording] = useState<string | null>(null);
  const [find, setFind] = useState('');

  const binds = useMemo(() => settings?.keybinds ?? {}, [settings?.keybinds]);
  const boundCount = ACTIONS.filter((a) => binds[a.id]).length;

  /* Ids from older versions that no longer exist are tidied away. */
  useEffect(() => {
    const known = new Set<string>(ACTIONS.map((a) => a.id));
    const stale = Object.keys(binds).filter((a) => !known.has(a));
    if (!stale.length) return;
    const next = { ...binds };
    for (const a of stale) delete next[a];
    void saveSettings({ keybinds: next });
  }, [binds, saveSettings]);

  async function bind(action: string, combo: string) {
    const owner = comboOwner(binds, combo, action);
    if (owner) {
      toast(combo + ' is already used by ' + actionLabel(owner), 'warn');
      return;
    }
    await saveSettings({ keybinds: { ...binds, [action]: combo } });
    toast(actionLabel(action) + ' is now ' + combo, 'good');
  }

  async function clear(action: string) {
    const next = { ...binds };
    delete next[action];
    await saveSettings({ keybinds: next });
  }

  async function useRecommended() {
    const next = { ...binds };
    let added = 0;
    for (const a of ACTIONS) {
      if (next[a.id]) continue;
      if (comboOwner(next, a.suggested, a.id)) continue;
      next[a.id] = a.suggested;
      added++;
    }
    await saveSettings({ keybinds: next });
    toast(added ? added + ' keybinds added' : 'Everything already has a key', 'good');
  }

  async function clearAll() {
    if (!(await confirm('Clear every keybind?', 'Pugo will stop listening for all of them until you bind them again.', { confirmLabel: 'Clear all', danger: true }))) return;
    await saveSettings({ keybinds: {} });
  }

  const q = find.trim().toLowerCase();
  const matches = (a: ActionDef) => !q || (a.label + ' ' + a.description + ' ' + (binds[a.id] ?? '')).toLowerCase().includes(q);

  return (
    <>
      {/* ---- toolbar ---- */}
      <div className={cn(CARD, 'flex flex-wrap items-center gap-3')}>
        <label className="relative min-w-[200px] flex-1">
          <span className="sr-only">Search keybinds</span>
          <Icon name="search" size={16} className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-mute" />
          <input
            type="search"
            value={find}
            onChange={(e) => setFind(e.target.value)}
            placeholder="Search actions or keys"
            className="h-11 w-full rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 pr-3 pl-10 text-[14.5px] shadow-[inset_0_0_0_1px_var(--line)] outline-none transition-shadow duration-[250ms] placeholder:text-ghost focus:shadow-[inset_0_0_0_1px_var(--accent),0_0_0_3px_var(--accent-soft)]"
          />
        </label>
        <span className="text-[14px] text-mute tabular-nums">{boundCount} of {ACTIONS.length} bound</span>
        <button
          type="button"
          onClick={() => void useRecommended()}
          className="flex h-11 items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-accent px-4 text-[14.5px] font-bold text-accent-fg outline-none transition-[background-color,transform] duration-[250ms] hover:bg-accent-hi active:scale-[.98] focus-visible:shadow-[0_0_0_3px_var(--accent-soft)]"
        >
          <Icon name="sparkles" size={17} />
          Use recommended
        </button>
        {boundCount ? (
          <button
            type="button"
            onClick={() => void clearAll()}
            className="h-11 rounded-[calc(12px*var(--radius-mul,1))] px-3 text-[14px] font-semibold text-mute outline-none transition-colors duration-[250ms] hover:text-bad focus-visible:shadow-[0_0_0_2px_var(--accent)]"
          >
            Clear all
          </button>
        ) : null}
      </div>

      {/* ---- the actions, by group ---- */}
      {GROUPS.map((g) => {
        const list = ACTIONS.filter((a) => a.group === g.id && matches(a));
        if (!list.length) return null;
        return (
          <section key={g.id} aria-labelledby={'kb-' + g.id} className={CARD}>
            <div className="mb-2 flex items-baseline gap-3">
              <h3 id={'kb-' + g.id} className="m-0 text-[16.5px] font-bold text-ink">{g.label}</h3>
              <span className="text-[13.5px] text-mute">{g.note}</span>
            </div>
            <ul className="m-0 flex list-none flex-col p-0">
              {list.map((a) => (
                <ActionRow
                  key={a.id}
                  action={a}
                  combo={binds[a.id] ?? ''}
                  recording={recording === a.id}
                  onRecord={() => setRecording(recording === a.id ? null : a.id)}
                  onCapture={(combo) => { setRecording(null); if (combo) void bind(a.id, combo); }}
                  onClear={() => { setRecording(null); void clear(a.id); }}
                />
              ))}
            </ul>
          </section>
        );
      })}

      {/* ---- the window's own ---- */}
      <section className={CARD}>
        <div className="mb-2 flex items-baseline gap-3">
          <h3 className="m-0 text-[16.5px] font-bold text-ink">Always on</h3>
          <span className="text-[13.5px] text-mute">Part of the app; these cannot be changed.</span>
        </div>
        <ul className="m-0 flex list-none flex-col p-0">
          {([['palette', 'search', 'Search', 'Find anything - people, games, parties, settings'], ['settings', 'settings', 'Settings', 'Open settings']] as const).map(([id, icon, text, note]) => (
            <li key={id} className="flex items-center gap-4 border-b border-line-soft py-3 last:border-b-0">
              <span className="grid h-10 w-10 flex-none place-items-center rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 text-mute"><Icon name={icon} size={18} /></span>
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-semibold text-ink">{text}</span>
                <span className="block text-[13.5px] text-mute">{note}</span>
              </span>
              <Keys combo={BUILTIN[id]} />
            </li>
          ))}
        </ul>
      </section>
      {dialog}
    </>
  );
}

function ActionRow({ action, combo, recording, onRecord, onCapture, onClear }: {
  action: ActionDef;
  combo: string;
  recording: boolean;
  onRecord: () => void;
  onCapture: (combo: string) => void;
  onClear: () => void;
}) {
  const blocked = useHotkeyFailures().includes(action.id);
  const desktop = isDesktop();

  /* While recording, every key belongs to this row. */
  useEffect(() => {
    if (!recording) return;
    function onKey(e: KeyboardEvent) {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (e.key === 'Escape') { onCapture(''); return; }
      if ((e.key === 'Backspace' || e.key === 'Delete') && !e.ctrlKey && !e.altKey && !e.shiftKey) { onClear(); return; }
      const next = comboOf(e);
      if (next) onCapture(next);
    }
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [recording, onCapture, onClear]);

  return (
    <li className="group flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line-soft py-3 last:border-b-0">
      <span className={cn(
        'grid h-10 w-10 flex-none place-items-center rounded-[calc(12px*var(--radius-mul,1))] transition-colors duration-[250ms]',
        combo ? 'bg-accent-soft text-accent' : 'bg-surface-2 text-mute'
      )}>
        <Icon name={action.icon} size={18} />
      </span>
      <span className="min-w-[180px] flex-1">
        <span className="flex items-center gap-2 text-[15px] font-semibold text-ink">
          {action.label}
          {desktop && isGlobalAction(action.id) ? (
            <span className="rounded-full bg-[color-mix(in_srgb,var(--good)_14%,transparent)] px-2 py-px text-[11.5px] font-semibold text-good">Works in games</span>
          ) : null}
        </span>
        <span className={cn('block text-[13.5px]', blocked && combo ? 'text-warn' : 'text-mute')}>
          {blocked && combo ? 'Another program owns this combination - it only works inside Pugo.' : action.description}
        </span>
      </span>

      <button
        type="button"
        onClick={onRecord}
        aria-label={recording ? 'Recording a key for ' + action.label + '. Press keys, Esc to cancel.' : 'Set a key for ' + action.label}
        className={cn(
          'flex h-11 min-w-[150px] items-center justify-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] px-3 outline-none',
          'transition-[background-color,box-shadow,color] duration-[250ms] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
          recording
            ? 'bg-accent-soft text-accent shadow-[inset_0_0_0_1.5px_var(--accent)]'
            : combo
              ? 'bg-surface-2 shadow-[inset_0_0_0_1px_var(--line-soft)] hover:shadow-[inset_0_0_0_1px_var(--accent-line)]'
              : 'bg-surface-2 text-mute shadow-[inset_0_0_0_1px_var(--line-soft)] hover:text-ink hover:shadow-[inset_0_0_0_1px_var(--accent-line)]'
        )}
      >
        {recording ? (
          <span className="flex items-center gap-2 text-[13.5px] font-semibold">
            <span className="relative flex h-2 w-2"><span className="absolute inset-0 animate-ping rounded-full bg-accent opacity-60" /><span className="relative h-2 w-2 rounded-full bg-accent" /></span>
            Press keys…
          </span>
        ) : combo ? (
          <Keys combo={combo} />
        ) : (
          <span className="text-[13.5px] font-medium">Click to set</span>
        )}
      </button>

      <button
        type="button"
        onClick={onClear}
        aria-label={'Remove the key for ' + action.label}
        title="Remove"
        className={cn(
          'grid h-9 w-9 flex-none place-items-center rounded-[10px] text-mute outline-none transition-[opacity,background-color,color] duration-[250ms] hover:bg-[color-mix(in_srgb,var(--bad)_14%,transparent)] hover:text-bad focus-visible:opacity-100 focus-visible:shadow-[0_0_0_2px_var(--accent)]',
          combo ? 'opacity-0 group-hover:opacity-100' : 'invisible'
        )}
      >
        <Icon name="close" size={15} />
      </button>
    </li>
  );
}

/** A combination drawn as keycaps. */
export function Keys({ combo }: { combo: string }) {
  return (
    <span className="flex items-center gap-1">
      {comboParts(combo).map((part, i) => (
        <kbd
          key={i}
          className="min-w-[28px] rounded-[calc(8px*var(--radius-mul,1))] bg-surface-3 px-2 py-1 text-center font-mono text-[12.5px] font-bold text-ink shadow-[inset_0_-2px_0_var(--line),inset_0_0_0_1px_var(--line-soft)]"
        >
          {part}
        </kbd>
      ))}
    </span>
  );
}
